import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Maximize, User, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { portfolio } from '../data/mock';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const foundProject = portfolio.find(p => p.id === parseInt(id));
    if (foundProject) {
      setProject(foundProject);
    }
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Projet non trouvé</p>
      </div>
    );
  }

  const scrollToContact = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header Spacing */}
      <div className="h-24"></div>

      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-[#0B1F3C] hover:text-[#c9a961] flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Retour aux réalisations
        </Button>
      </div>

      <div className="container mx-auto px-4 pb-20">
        {/* Project Header */}
        <div className="mb-12">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge className="bg-[#c9a961] text-white border-0 px-4 py-2 text-sm">
              {project.category}
            </Badge>
            <span className="text-gray-500">•</span>
            <span className="text-gray-600">{project.year}</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3C] mb-4">
            {project.title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-6">
            {project.description}
          </p>

          {/* Project Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#c9a961]/10 rounded-lg flex items-center justify-center">
                    <MapPin className="text-[#c9a961]" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Localisation</p>
                    <p className="font-semibold text-[#0B1F3C]">{project.location}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#c9a961]/10 rounded-lg flex items-center justify-center">
                    <Maximize className="text-[#c9a961]" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Surface</p>
                    <p className="font-semibold text-[#0B1F3C]">{project.surface}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#c9a961]/10 rounded-lg flex items-center justify-center">
                    <Calendar className="text-[#c9a961]" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Durée</p>
                    <p className="font-semibold text-[#0B1F3C]">{project.duration}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#c9a961]/10 rounded-lg flex items-center justify-center">
                    <User className="text-[#c9a961]" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Client</p>
                    <p className="font-semibold text-[#0B1F3C]">{project.client}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Gallery */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-[#0B1F3C] mb-6">Galerie Photos</h2>
          
          {/* Main Image */}
          <div className="mb-4 rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={project.gallery[selectedImage]}
              alt={`${project.title} - Photo ${selectedImage + 1}`}
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* Thumbnails */}
          <div className="grid grid-cols-4 gap-4">
            {project.gallery.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`rounded-lg overflow-hidden transition-all duration-300 ${
                  selectedImage === index
                    ? 'ring-4 ring-[#c9a961] scale-105'
                    : 'hover:scale-105 opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-24 object-cover"
                />
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#0B1F3C] mb-4">
                  Description du projet
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {project.fullDescription}
                </p>
              </CardContent>
            </Card>

            {/* Tasks Performed */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#0B1F3C] mb-6">
                  Travaux réalisés
                </h2>
                <div className="space-y-6">
                  {project.tasks.map((task, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#c9a961] to-[#b89951] rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {index + 1}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#0B1F3C] mb-2">
                          {task.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {task.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Features */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0B1F3C] mb-4">
                  Points forts
                </h3>
                <ul className="space-y-3">
                  {project.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-[#c9a961] flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-[#0B1F3C] to-[#2a4a62] text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-3">
                  Un projet similaire ?
                </h3>
                <p className="text-white/90 mb-6">
                  Confiez-nous votre projet de rénovation. Nous vous accompagnons de A à Z.
                </p>
                <Button
                  onClick={scrollToContact}
                  className="w-full bg-[#c9a961] hover:bg-[#b89951] text-white font-semibold"
                >
                  Demander un devis gratuit
                </Button>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0B1F3C] mb-4">
                  Contactez-nous
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">📞</span>
                    <a href="tel:0666770127" className="hover:text-[#c9a961] transition-colors">
                      06 66 77 01 27
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">✉️</span>
                    <a href="mailto:concept.domus.co@gmail.com" className="hover:text-[#c9a961] transition-colors break-all">
                      concept.domus.co@gmail.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
